import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

export function useMyData() {
  return useQuery({
    queryKey: ['my-campaigns'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('campaign_members')
        .select(`
          id, role, display_name, unit_preference,
          campaigns (id, name, description, setting, current_tr_year, gm_user_id, created_at)
        `)
        .eq('user_id', user.id)

      if (error) throw error
      return data
    },
  })
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: ['campaign', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', id!)
        .single()

      if (error) throw error
      return data
    },
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { name: string; description?: string; setting?: string }) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .insert({
          name: params.name,
          description: params.description,
          setting: params.setting ?? 'Kaldor',
          gm_user_id: user.id,
        })
        .select()
        .single()

      if (campaignError) throw campaignError

      const { error: memberError } = await supabase
        .from('campaign_members')
        .insert({
          campaign_id: campaign.id,
          user_id: user.id,
          role: 'gm',
        })

      if (memberError) throw memberError

      return campaign
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-campaigns'] })
    },
  })
}

export function useCampaignPlayers(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaign-players', campaignId],
    enabled: !!campaignId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_members')
        .select('user_id, display_name')
        .eq('campaign_id', campaignId!)
        .eq('role', 'player')
      if (error) throw error
      return (data ?? []) as Array<{ user_id: string; display_name: string }>
    },
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (campaignId: string) => {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', campaignId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-campaigns'] })
    },
  })
}
